import { ApiError, ApiErrorSource } from "src/core/apiError";
import { logger } from "src/core/globals";
import { RestError } from "src/gen/schema/common/error";
import {
  clearActiveApiOrigin,
  getRestUrl,
  updateActiveApiOriginFromFetchResponse
} from "src/utils/activeApiEndpoint";
import { Constants } from "src/utils/constants";

const { SESSION_LOGOUT } = Constants;

/**
 * HTTP Status code for Unauthorized.
 */
const UNAUTHORIZED: number = 401;

export type RestApiContext = {
  method: string;
  url: string;
  requestBody?: string;
  requestTs: Date;
  status: number;
  durationMs: number;
  response: object;
  responseTs: Date;
  isError: boolean;
};

export const parseRestError = (json: unknown): RestError => {
  const rawError =
    json && typeof json === "object" ? (json as Record<string, unknown>) : {};
  const errCode = rawError.errCode;
  return RestError.fromJSON({
    ...rawError,
    errCode:
      typeof errCode === "string" && /^\d+$/.test(errCode)
        ? Number(errCode)
        : errCode
  });
};

/**
 * Serializes a key-value pair into an array of tuples suitable for URLSearchParams.
 * - Arrays are serialized as multiple entries with the same key.
 * - Dates are serialized as ISO strings.
 * - Nested objects are serialized with dot notation.
 *
 * @param key
 * @param value
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const serializeParam = (key: string, value: any): [string, string][] => {
  if (Array.isArray(value)) {
    return value.map((item) => [key, item]);
  }
  if (value instanceof Date) {
    return [[key, value.toISOString()]];
  }
  if (value.constructor === Object) {
    return Object.entries(value).flatMap(([field, value]) =>
      serializeParam(`${key}.${field}`, value)
    );
  }
  return value ? [[key, String(value)]] : [];
};

/**
 * Generates a query param string from the given request object.
 * @param request
 * @param ignoreFields
 */
export const generateQueryParams = (
  request: Record<string, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  ignoreFields: string[] = []
) => {
  const params = Object.entries(request)
    .filter(([key, value]) => value && !ignoreFields.includes(key))
    .flatMap(([key, value]) => serializeParam(key, value));

  const queryParams = new URLSearchParams(
    params as [string, string][]
  ).toString();

  return queryParams ? `?${queryParams}` : "";
};

/**
 * Generates a JSON payload from the given request object and model.
 *
 * @param request
 * @param model
 * @param fieldName
 */
export const generatePayload = <T>(
  request: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  model: { toJSON: (obj: T) => any }, // eslint-disable-line @typescript-eslint/no-explicit-any
  fieldName?: string | undefined
): string => {
  if (!request) {
    return "";
  }
  if (!fieldName || !request[fieldName]) {
    return JSON.stringify(model.toJSON(request as T));
  }
  const field = request[fieldName];
  return Array.isArray(field)
    ? JSON.stringify(field.map((o: T) => model.toJSON(o)))
    : JSON.stringify(model.toJSON(field as T));
};

/**
 * Sends an HTTP request using the Fetch API.
 *
 * @param {string} url - The URL to which the request is sent.
 * @param {string} method - The HTTP method to use (e.g., GET, POST, PUT, DELETE).
 * @param {string} [body] - The body of the request, if applicable.
 * @param {RequestInit} [options] - Additional options to customize the request.
 * @returns {Promise<Response>} A promise that resolves to the response of the request.
 */
const request = (
  url: string,
  method: string,
  body?: string,
  options?: RequestInit | undefined
): Promise<Response> => {
  const restHistoryEnabled = !!window?.cnc?.settings.get("restHistory");
  const apiLoggingInConsole = !!window?.cnc?.settings.get(
    "apiLoggingInConsole"
  );
  const shouldLog = restHistoryEnabled || apiLoggingInConsole;
  const requestTs = new Date();
  const headers = new Headers({
    "Content-Type": "application/json",
    ...options?.headers
  });
  const opts: RequestInit | undefined = {
    method: method,
    mode: "cors",
    cache: "no-cache",
    credentials: "include",
    redirect: "follow",
    referrerPolicy: "no-referrer",
    body,
    ...options,
    headers
  };

  const logRestCall = (
    logObject: RestApiContext,
    historyEnabled: boolean,
    consoleEnabled: boolean
  ) => {
    if (historyEnabled) {
      window.cnc?.logRestContext(logObject);
    }
    if (consoleEnabled) {
      if (logObject.isError) {
        logger.error(logObject);
      } else {
        logger.log(logObject);
      }
    }
  };

  const buildErrorContext = (
    method: string,
    url: string,
    requestBody: string | undefined,
    requestTs: Date,
    responseTs: Date,
    status: number,
    responseBody: object
  ): RestApiContext => ({
    method,
    url,
    requestBody,
    requestTs,
    status,
    durationMs: responseTs.getTime() - requestTs.getTime(),
    response: responseBody,
    responseTs,
    isError: true
  });

  const restUrl = getRestUrl(url);

  return fetch(restUrl, opts)
    .then((response: Response) => {
      updateActiveApiOriginFromFetchResponse(response);

      if (response.ok) {
        if (shouldLog) {
          const responseTs = new Date();
          const status = response.status;
          const durationMs = responseTs.getTime() - requestTs.getTime();
          response
            .clone()
            .text()
            .then((text) => {
              let parsedBody: object;
              try {
                parsedBody = JSON.parse(text);
              } catch {
                parsedBody = { body: text };
              }
              logRestCall(
                {
                  method,
                  url: restUrl,
                  requestBody: body,
                  requestTs,
                  status,
                  durationMs,
                  response: parsedBody,
                  responseTs,
                  isError: false
                },
                restHistoryEnabled,
                apiLoggingInConsole
              );
            });
        }
        return Promise.resolve(response);
      } else {
        switch (response.status) {
          case UNAUTHORIZED:
            clearActiveApiOrigin();
            if (shouldLog) {
              const responseTs = new Date();
              logRestCall(
                buildErrorContext(
                  method,
                  restUrl,
                  body,
                  requestTs,
                  responseTs,
                  response.status,
                  {}
                ),
                restHistoryEnabled,
                apiLoggingInConsole
              );
            }
            document.body.dispatchEvent(SESSION_LOGOUT);
            return Promise.reject(response);
          default:
            return response.json().then(
              (json: RestError) => {
                const restError = parseRestError(json);
                if (shouldLog) {
                  const responseTs = new Date();
                  logRestCall(
                    buildErrorContext(
                      method,
                      restUrl,
                      body,
                      requestTs,
                      responseTs,
                      response.status,
                      restError
                    ),
                    restHistoryEnabled,
                    apiLoggingInConsole
                  );
                }
                return Promise.reject(ApiError.fromRestError(restError));
              },
              (err) => {
                if (shouldLog) {
                  const responseTs = new Date();
                  logRestCall(
                    buildErrorContext(
                      method,
                      restUrl,
                      body,
                      requestTs,
                      responseTs,
                      response.status,
                      {
                        message: err.message || err.toString()
                      }
                    ),
                    restHistoryEnabled,
                    apiLoggingInConsole
                  );
                }
                return Promise.reject(
                  ApiError.fromDebugMessage(
                    `Rest ${method.toUpperCase()} call to URL "${restUrl}" failed with status ${
                      response.status
                    }. Error parsing response body: ${
                      err.message || err.toString()
                    }`,
                    ApiErrorSource.Rest
                  )
                );
              }
            );
        }
      }
    })
    .catch((err: unknown) => {
      if (err instanceof ApiError || err instanceof Response) {
        return Promise.reject(err);
      }
      const message = err instanceof Error ? err.message : String(err);
      if (shouldLog) {
        const responseTs = new Date();
        logRestCall(
          buildErrorContext(method, restUrl, body, requestTs, responseTs, 0, {
            message
          }),
          restHistoryEnabled,
          apiLoggingInConsole
        );
      }
      return Promise.reject(
        ApiError.fromDebugMessage(
          `Rest ${method.toUpperCase()} call to URL "${restUrl}" failed before receiving a response: ${message}`,
          ApiErrorSource.Rest
        )
      );
    });
};

export const ReST = {
  get: (url: string, options?: RequestInit | undefined): Promise<Response> => {
    return request(url, "GET", undefined, options);
  },
  post: (
    url: string,
    body: string = "",
    options?: RequestInit | undefined
  ): Promise<Response> => {
    return request(url, "POST", body, options);
  },
  put: (
    url: string,
    body: string = "",
    options?: RequestInit | undefined
  ): Promise<Response> => {
    return request(url, "PUT", body, options);
  },
  delete: (
    url: string,
    options?: RequestInit | undefined
  ): Promise<Response> => {
    return request(url, "DELETE", undefined, options);
  }
};
